/**
 * The support API, as a person sees it: their own tickets, and nobody else's.
 *
 * Every call carries a bearer token for support's audience (`support-token.ts`)
 * and never the cookie, which support does not read. Support scopes each of
 * these routes to the token's own subject, so there is no parameter here that
 * could name somebody else.
 */
import { supportToken, type TokenSource } from './support-token.js';

export const SUPPORT_URL = (import.meta.env.VITE_SUPPORT_URL ?? '').replace(/\/$/, '');

export type TicketStatus = 'OPEN' | 'WAITING_ON_USER' | 'WAITING_ON_SUPPORT' | 'RESOLVED';

export interface SupportProduct {
  slug: string;
  name: string;
}

export interface TicketRow {
  reference: string;
  subject: string;
  product: string;
  status: TicketStatus;
  openedAt: string;
  updatedAt: string;
  lastMessage: { direction: string; at: string; preview: string } | null;
}

export interface MessageAttachment {
  id: string;
  filename: string;
  bytes: number;
  isDiagnosticLog: boolean;
}

export interface TicketMessage {
  id: string;
  direction: 'INBOUND' | 'OUTBOUND' | 'SYSTEM';
  /** Their request, typed by staff who opened the ticket for them. */
  writtenByStaff?: boolean;
  /**
   * A reply an AI agent sent on its own (support#81), shown as an "AI agent"
   * chip. Absent from a support build older than support#82: no chip.
   */
  aiWritten?: boolean;
  at: string;
  body: string;
  /** Optional on the type: a support older than attachments sends none. */
  attachments?: MessageAttachment[];
}

/** Where to put one file, from support's `POST /uploads`. */
export interface SignedUpload {
  key: string;
  uploadUrl: string;
  expiresInSeconds: number;
  requiredHeaders: Record<string, string>;
}

export interface Ticket {
  reference: string;
  subject: string;
  product: string;
  status: TicketStatus;
  openedAt: string;
  updatedAt: string;
  messages: TicketMessage[];
}

/** Support's own refusal: a sentence for a person, and a code to branch on. */
export class SupportError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message);
    this.name = 'SupportError';
  }
}

async function request<T>(method: 'GET' | 'POST', path: string, token: TokenSource, fetchImpl: typeof fetch, body?: unknown): Promise<T> {
  if (!SUPPORT_URL && fetchImpl === fetch) {
    throw new SupportError('Support is not available here yet.', 0, 'NOT_CONFIGURED');
  }
  const res = await fetchImpl(`${SUPPORT_URL}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${await token()}`,
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    credentials: 'omit',
  });
  if (res.ok) return (await res.json()) as T;
  const parsed = (await res.json().catch(() => null)) as { error?: { code?: string; message?: string } } | null;
  throw new SupportError(
    parsed?.error?.message ?? `Support could not be reached (${res.status}). Try again in a moment.`,
    res.status,
    parsed?.error?.code,
  );
}

export function supportApi(token: TokenSource = supportToken, fetchImpl: typeof fetch = fetch) {
  const get = <T>(path: string) => request<T>('GET', path, token, fetchImpl);
  const post = <T>(path: string, body: unknown = {}) => request<T>('POST', path, token, fetchImpl, body);
  const one = (reference: string) => `/tickets/${encodeURIComponent(reference)}`;

  return {
    products: () => get<{ products: SupportProduct[] }>('/products'),
    tickets: () => get<{ tickets: TicketRow[] }>('/tickets'),
    ticket: (reference: string) => get<Ticket>(one(reference)),
    /**
     * The topics the Account portal form lists for a product (auth#672), in
     * the order staff set in the admin portal. Public on support's side and
     * sent with no token: it is the same list anybody sees on the public form.
     */
    topics: async (product: string): Promise<{ topics: string[] }> => {
      if (!SUPPORT_URL && fetchImpl === fetch) {
        throw new SupportError('Support is not available here yet.', 0, 'NOT_CONFIGURED');
      }
      const query = new URLSearchParams({ form: 'ACCOUNT_PORTAL', product });
      const res = await fetchImpl(`${SUPPORT_URL}/topics?${query.toString()}`, { method: 'GET', credentials: 'omit' });
      if (!res.ok) throw new SupportError(`Topics could not be loaded (${res.status}).`, res.status);
      return (await res.json()) as { topics: string[] };
    },
    open: (input: { product: string; subject: string; body: string; topic?: string; attachmentKeys?: string[] }) =>
      post<{ reference: string; status: TicketStatus }>('/tickets', { ...input, channel: 'ACCOUNT_PORTAL' }),
    reply: (reference: string, body: string, attachmentKeys?: string[]) =>
      post<{ id: string; at: string }>(`${one(reference)}/messages`, {
        body,
        ...(attachmentKeys?.length ? { attachmentKeys } : {}),
      }),
    /** Somewhere to put a file. Support checks the type and size and signs both. */
    signUpload: (file: { filename: string; contentType: string; bytes: number }) =>
      post<SignedUpload>('/uploads', { ...file, kind: 'file' }),
    /**
     * The file itself, straight to storage: never through support, and never
     * with the support token, which the storage URL does not want and must not
     * see. `content-length` is not set here because a browser refuses to; it
     * sends the file's own length, which is the length that was signed.
     */
    putFile: async (upload: SignedUpload, file: Blob) => {
      const headers = Object.fromEntries(
        Object.entries(upload.requiredHeaders).filter(([name]) => name.toLowerCase() !== 'content-length'),
      );
      const res = await fetchImpl(upload.uploadUrl, { method: 'PUT', headers, body: file, credentials: 'omit' });
      if (!res.ok) throw new SupportError(`The file could not be uploaded (${res.status}). Try again.`, res.status);
    },
    /** A short-lived link to one file on one of their own requests. */
    attachmentUrl: (reference: string, id: string) =>
      get<{ url: string; filename: string }>(`${one(reference)}/attachments/${encodeURIComponent(id)}`),
    resolve: (reference: string) => post<{ reference: string; status: TicketStatus }>(`${one(reference)}/resolve`),
    /**
     * Feedback, which support keeps and never answers as a thread: an id comes
     * back, not a reference, and there is no route to list it again.
     */
    feedback: (input: { product: string; kind: FeedbackKind; message: string; mayContact: boolean }) =>
      post<{ id: string }>('/feedback', { ...input, channel: 'ACCOUNT_PORTAL' }),
  };
}

export type SupportApi = ReturnType<typeof supportApi>;

// ── Words and paths, one place ─────────────────────────────────────────

/** Whose move it is, said to the person rather than to staff. */
export const STATUS_WORDS: Record<TicketStatus, string> = {
  OPEN: 'Open',
  WAITING_ON_SUPPORT: 'With our team',
  WAITING_ON_USER: 'Waiting for your reply',
  RESOLVED: 'Resolved',
};

export const statusWords = (status: string): string => STATUS_WORDS[status as TicketStatus] ?? status;

/** Support's own limits (its `schemas.ts`), so the form can say what it needs. */
export const SUBJECT_MIN = 3;
export const BODY_MIN = 10;

/**
 * The topic that lets a person write their own subject (auth#672). Support
 * always lists it last, and it is all the form offers when the list cannot be
 * loaded. It is not sent as a topic: a ticket without one is routed by the
 * product's any-topic rule.
 */
export const SOMETHING_ELSE = 'Something else';

/** Topics as the form shows them: support's list, with "Something else" last and once. */
export function topicChoices(topics: readonly string[] | null | undefined): string[] {
  const rest = (topics ?? []).filter((t) => t.trim() && t !== SOMETHING_ELSE);
  return [...new Set(rest), SOMETHING_ELSE];
}

/**
 * What the ticket is sent with: the topic as its subject, unless it is
 * "Something else", when the person's own subject goes and no topic does.
 */
export function requestSubject(f: { topic: string; subject: string }): { subject: string; topic?: string } {
  return f.topic === SOMETHING_ELSE ? { subject: f.subject.trim() } : { subject: f.topic, topic: f.topic };
}

/** What is missing from a new request, or null when it can be sent. */
export function newRequestProblem(f: { product: string; topic: string; subject: string; body: string }): string | null {
  if (!f.product) return 'Choose what it is about.';
  if (!f.topic) return 'Choose a topic.';
  if (f.topic === SOMETHING_ELSE && f.subject.trim().length < SUBJECT_MIN) return `A subject of at least ${SUBJECT_MIN} characters.`;
  if (f.body.trim().length < BODY_MIN) return `A description of at least ${BODY_MIN} characters.`;
  return null;
}

// ── Feedback ────────────────────────────────────────────────────────

/** Support's `FeedbackKind`, in the order the form offers them. */
export type FeedbackKind = 'IDEA' | 'BUG' | 'COMPLAINT' | 'PRAISE';

export const FEEDBACK_KINDS: ReadonlyArray<{ kind: FeedbackKind; label: string }> = [
  { kind: 'IDEA', label: 'An idea' },
  { kind: 'BUG', label: 'Something is not working' },
  { kind: 'COMPLAINT', label: 'A complaint' },
  { kind: 'PRAISE', label: 'Praise' },
];

/** Support's own limits on a message (its `createFeedbackInput`). */
export const FEEDBACK_MIN = 3;
export const FEEDBACK_MAX = 4000;

/** What is missing from feedback, or null when it can be sent. */
export function feedbackProblem(f: { product: string; message: string }): string | null {
  if (!f.product) return 'Choose which product it is about.';
  if (f.message.trim().length < FEEDBACK_MIN) return 'Say a little more. A sentence is enough.';
  return null;
}

export const ticketPath = (reference: string) => `/support/${encodeURIComponent(reference)}`;

/** The reference in `/support/<reference>`, or null on any other path. */
export function referenceOf(path: string): string | null {
  const match = /^\/support\/([^/]+)$/.exec(path);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]!).toUpperCase();
  } catch {
    return null;
  }
}

// ── Files ───────────────────────────────────────────────────────────

/**
 * What support accepts, restated so a wrong file is refused before it is sent
 * anywhere. Support's `lib/attachments.ts` is the source and still decides; a
 * file that passes here and fails there shows support's own sentence.
 */
export const ACCEPTED_TYPES: Readonly<Record<string, string>> = {
  'image/png': 'PNG',
  'image/jpeg': 'JPEG',
  'image/heic': 'HEIC',
  'image/heif': 'HEIF',
  'image/webp': 'WebP',
  'application/pdf': 'PDF',
};
export const MAX_FILE_BYTES = 25 * 1024 * 1024;
export const MAX_FILES = 10;

/** For the file picker's `accept`: types and the extensions a phone labels them with. */
export const ACCEPT_ATTRIBUTE = [...Object.keys(ACCEPTED_TYPES), '.heic', '.heif'].join(',');

/**
 * Why this file cannot be attached, or null. HEIC from an iPhone often arrives
 * with no type at all, so an empty type is judged by the name's extension.
 */
export function fileProblem(file: { name: string; type: string; size: number }): string | null {
  const type = contentTypeOf(file);
  if (!type) return `${file.name} is not a picture or a PDF, so it cannot be attached.`;
  if (file.size > MAX_FILE_BYTES) return `${file.name} is ${formatBytes(file.size)}. The limit is 25 MB.`;
  if (file.size === 0) return `${file.name} is empty.`;
  return null;
}

/** The type to declare for a file, or null when support would refuse it. */
export function contentTypeOf(file: { name: string; type: string }): string | null {
  const declared = file.type.trim().toLowerCase();
  if (declared) return ACCEPTED_TYPES[declared] ? declared : null;
  const ext = file.name.toLowerCase().split('.').pop();
  return ext === 'heic' ? 'image/heic' : ext === 'heif' ? 'image/heif' : null;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/** Who a message is from, in the person's own thread. */
export function authorOf(m: Pick<TicketMessage, 'direction' | 'writtenByStaff'>): string {
  if (m.direction === 'OUTBOUND') return 'Ridgewill support';
  if (m.direction === 'SYSTEM') return 'Update';
  return m.writtenByStaff ? 'Opened for you by Ridgewill support' : 'You';
}
